import { isMuteActive } from '../contracts/chat-notifications.js';
import {
  hasPermission,
  PermissionFlags,
  THREAD_CHANNEL_TYPES,
} from '../contracts/chat-permissions.js';
import type {
  APIChannel,
  APIMessage,
  APIRole,
  APIUserGuildSettings,
} from '../contracts/chat-types.js';
import { ChannelType } from '../contracts/chat-types.js';
import { compareSnowflakes } from '../contracts/snowflake.js';
import { can, channelPermissions, sortedRoles } from '../permissoes/index.js';
import {
  type ChatState,
  DMS_SETTINGS_KEY,
  type GuildRecord,
  type MemberRecord,
  type PendingMessage,
  type Snowflake,
} from './state.js';

/**
 * Seletores: funções puras sobre o estado. Muitos devolvem listas novas a
 * cada chamada — na interface, use com `useShallow` (zustand) ou `useMemo`
 * sobre as fatias estáveis (`channels`, `messages[canal]`…).
 */

// ---------------------------------------------------------------------------
// Servidores e canais
// ---------------------------------------------------------------------------

/**
 * Servidores na ordem da barra: os sem lugar em `guild_positions` (recém
 * criados ou entrados) ficam no topo, do mais novo para o mais antigo, como
 * no Discord; depois, a ordem salva.
 */
export function selectGuilds(state: ChatState): GuildRecord[] {
  const order = state.userSettings?.guild_positions ?? [];
  const position = new Map(order.map((id, index) => [id, index]));
  const joined = (id: Snowflake) => {
    const at = state.guilds[id]?.joined_at;
    return at ? Date.parse(at) : 0;
  };
  return [...state.guildIds]
    .sort((a, b) => {
      const pa = position.get(a);
      const pb = position.get(b);
      if (pa !== undefined && pb !== undefined) return pa - pb;
      if (pa !== undefined) return 1;
      if (pb !== undefined) return -1;
      return (
        joined(b) - joined(a) ||
        state.guildIds.indexOf(a) - state.guildIds.indexOf(b)
      );
    })
    .map((id) => state.guilds[id])
    .filter(Boolean);
}

export type ChannelGroup = {
  /** Nulo: os canais sem categoria (ficam no topo, como no Discord). */
  category: APIChannel | null;
  channels: APIChannel[];
};

const TEXT_LIKE = new Set<number>([
  ChannelType.GUILD_TEXT,
  ChannelType.GUILD_ANNOUNCEMENT,
  ChannelType.GUILD_FORUM,
  ChannelType.GUILD_MEDIA,
]);

/** Ordem do Discord: texto antes de voz, depois `position`, depois ID. */
function compareChannels(a: APIChannel, b: APIChannel): number {
  const voice = (channel: APIChannel) =>
    channel.legacy_voice || !TEXT_LIKE.has(channel.type) ? 1 : 0;
  return (
    voice(a) - voice(b) ||
    (a.position ?? 0) - (b.position ?? 0) ||
    compareSnowflakes(a.id, b.id)
  );
}

/** Canais do servidor agrupados por categoria, na ordem da barra lateral. */
export function selectChannelTree(
  state: ChatState,
  guildId: Snowflake,
): ChannelGroup[] {
  const channels = (state.guildChannelIds[guildId] ?? [])
    .map((id) => state.channels[id])
    .filter(Boolean);
  const categories = channels
    .filter((channel) => channel.type === ChannelType.GUILD_CATEGORY)
    .sort(
      (a, b) =>
        (a.position ?? 0) - (b.position ?? 0) || compareSnowflakes(a.id, b.id),
    );
  const byParent = new Map<string | null, APIChannel[]>();
  const categoryIds = new Set(categories.map((category) => category.id));
  for (const channel of channels) {
    if (channel.type === ChannelType.GUILD_CATEGORY) continue;
    const parent =
      channel.parent_id && categoryIds.has(channel.parent_id)
        ? channel.parent_id
        : null;
    byParent.set(parent, [...(byParent.get(parent) ?? []), channel]);
  }
  const groups: ChannelGroup[] = [];
  const loose = (byParent.get(null) ?? []).sort(compareChannels);
  if (loose.length) groups.push({ category: null, channels: loose });
  for (const category of categories) {
    groups.push({
      category,
      channels: (byParent.get(category.id) ?? []).sort(compareChannels),
    });
  }
  return groups;
}

/**
 * A árvore da barra lateral: só os canais que a pessoa vê e, com "ocultar
 * canais silenciados" (menu do servidor), sem os silenciados que não têm
 * novidade. Categorias vazias somem.
 */
export function selectSidebarTree(
  state: ChatState,
  guildId: Snowflake,
): ChannelGroup[] {
  const hideMuted = !!state.guildSettings[guildId]?.hide_muted_channels;
  return selectChannelTree(state, guildId)
    .map((group) => ({
      ...group,
      channels: group.channels.filter(
        (channel) =>
          can(state, channel.id, PermissionFlags.VIEW_CHANNEL) &&
          !(
            hideMuted &&
            selectIsChannelMuted(state, channel.id) &&
            !selectIsUnread(state, channel.id)
          ),
      ),
    }))
    .filter((group) => group.channels.length > 0);
}

/**
 * Threads ativas de um canal que a pessoa vê; as em que ela entrou primeiro,
 * depois as mais recentes.
 */
export function selectActiveThreads(
  state: ChatState,
  parentId: Snowflake,
): APIChannel[] {
  const parent = state.channels[parentId];
  if (!parent?.guild_id) return [];
  return (state.guildThreadIds[parent.guild_id] ?? [])
    .map((id) => state.channels[id])
    .filter(
      (thread) =>
        thread &&
        thread.parent_id === parentId &&
        !thread.thread_metadata?.archived &&
        hasPermission(
          channelPermissions(state, thread.id),
          PermissionFlags.VIEW_CHANNEL,
        ),
    )
    .sort(
      (a, b) =>
        Number(!!state.joinedThreads[b.id]) -
          Number(!!state.joinedThreads[a.id]) ||
        compareSnowflakes(b.last_message_id ?? b.id, a.last_message_id ?? a.id),
    );
}

/** DMs e grupos, a conversa mais recente primeiro. */
export function selectPrivateChannels(state: ChatState): APIChannel[] {
  return state.privateChannelIds
    .map((id) => state.channels[id])
    .filter(Boolean)
    .sort((a, b) =>
      compareSnowflakes(b.last_message_id ?? b.id, a.last_message_id ?? a.id),
    );
}

// ---------------------------------------------------------------------------
// Mensagens
// ---------------------------------------------------------------------------

export function selectMessages(
  state: ChatState,
  channelId: Snowflake,
): APIMessage[] {
  const list = state.messages[channelId];
  return list ? list.ids.map((id) => list.byId[id]) : [];
}

export function selectPending(
  state: ChatState,
  channelId: Snowflake,
): PendingMessage[] {
  return state.pending[channelId] ?? [];
}

/** Quem está digitando no canal agora (sem eu). */
export function selectTypingUsers(
  state: ChatState,
  channelId: Snowflake,
  now: number = Date.now(),
): Snowflake[] {
  return Object.entries(state.typing[channelId] ?? {})
    .filter(([userId, expires]) => expires > now && userId !== state.me?.id)
    .map(([userId]) => userId);
}

// ---------------------------------------------------------------------------
// Leitura, menções e silêncio
// ---------------------------------------------------------------------------

/** Tem mensagem nova (o último ID do canal passou do lido). */
export function selectIsUnread(
  state: ChatState,
  channelId: Snowflake,
): boolean {
  const channel = state.channels[channelId];
  const last = channel?.last_message_id;
  if (!last || channel.type === ChannelType.GUILD_CATEGORY) return false;
  const read = state.readStates[channelId]?.last_message_id;
  return !read || compareSnowflakes(last, read) > 0;
}

export function selectMentionCount(
  state: ChatState,
  channelId: Snowflake,
): number {
  return state.readStates[channelId]?.mention_count ?? 0;
}

function settingsFor(
  state: ChatState,
  guildId: Snowflake | null | undefined,
): APIUserGuildSettings | undefined {
  return state.guildSettings[guildId ?? DMS_SETTINGS_KEY];
}

/** Canal silenciado (ele, a categoria ou — numa thread — o canal pai). */
export function selectIsChannelMuted(
  state: ChatState,
  channelId: Snowflake,
  now: Date = new Date(),
): boolean {
  const channel = state.channels[channelId];
  if (!channel) return false;
  const settings = settingsFor(state, channel.guild_id);
  if (!settings) return false;
  const chain = [channelId];
  if (channel.parent_id) {
    chain.push(channel.parent_id);
    const parent = state.channels[channel.parent_id];
    if (THREAD_CHANNEL_TYPES.has(channel.type) && parent?.parent_id) {
      chain.push(parent.parent_id);
    }
  }
  return settings.channel_overrides.some(
    (override) =>
      chain.includes(override.channel_id) &&
      isMuteActive(override.muted, override.mute_config, now),
  );
}

export function selectIsGuildMuted(
  state: ChatState,
  guildId: Snowflake,
  now: Date = new Date(),
): boolean {
  const settings = settingsFor(state, guildId);
  return !!settings && isMuteActive(settings.muted, settings.mute_config, now);
}

export type UnreadSummary = { unread: boolean; mentions: number };

/** Bolinha e contador do servidor: canais não silenciados com novidade. */
export function selectGuildUnread(
  state: ChatState,
  guildId: Snowflake,
  now: Date = new Date(),
): UnreadSummary {
  const guildMuted = selectIsGuildMuted(state, guildId, now);
  let unread = false;
  let mentions = 0;
  const ids = [
    ...(state.guildChannelIds[guildId] ?? []),
    ...(state.guildThreadIds[guildId] ?? []).filter(
      (id) => !!state.joinedThreads[id],
    ),
  ];
  for (const id of ids) {
    mentions += selectMentionCount(state, id);
    if (
      !unread &&
      !guildMuted &&
      selectIsUnread(state, id) &&
      !selectIsChannelMuted(state, id, now)
    ) {
      unread = true;
    }
  }
  return { unread, mentions };
}

/** DMs com mensagem nova (cada DM não lida aparece no topo da barra, como no Discord). */
export function selectUnreadPrivateChannels(
  state: ChatState,
  now: Date = new Date(),
): APIChannel[] {
  return selectPrivateChannels(state).filter(
    (channel) =>
      selectIsUnread(state, channel.id) &&
      !selectIsChannelMuted(state, channel.id, now),
  );
}

/** Número do ícone do app (menções + mensagens de DM não lidas). */
export function selectBadgeCount(state: ChatState): number {
  let total = 0;
  for (const read of Object.values(state.readStates)) {
    if (state.channels[read.id]) total += read.mention_count;
  }
  return total;
}

// ---------------------------------------------------------------------------
// Pessoas
// ---------------------------------------------------------------------------

/** Apelido no servidor → nome de exibição → nome de usuário. */
export function selectDisplayName(
  state: ChatState,
  userId: Snowflake,
  guildId?: Snowflake | null,
): string {
  const nick = guildId ? state.members[guildId]?.[userId]?.nick : null;
  const user = state.users[userId];
  return nick || user?.global_name || user?.username || 'desconhecido';
}

/** Cor do cargo colorido mais alto (nula = sem cor). */
export function selectMemberColor(
  state: ChatState,
  guildId: Snowflake,
  userId: Snowflake,
): number | null {
  const member = state.members[guildId]?.[userId];
  if (!member) return null;
  const mine = new Set(member.roles);
  const role = sortedRoles(state, guildId).find(
    (item) => mine.has(item.id) && item.color !== 0,
  );
  return role?.color ?? null;
}

export function selectPresence(
  state: ChatState,
  userId: Snowflake,
): 'online' | 'idle' | 'dnd' | 'offline' {
  return state.presences[userId]?.status ?? 'offline';
}

export type MemberGroup = {
  /** ID do cargo separado, ou `online`/`offline`. */
  id: string;
  role: APIRole | null;
  members: MemberRecord[];
};

/**
 * Lista de membros como a do Discord: quem está online, separado pelo cargo
 * mais alto que aparece à parte (`hoist`); depois "Online"; por último
 * "Offline". Num canal, só quem vê o canal.
 */
export function selectMemberGroups(
  state: ChatState,
  guildId: Snowflake,
  channelId?: Snowflake,
): MemberGroup[] {
  const members = Object.values(state.members[guildId] ?? {});
  const roles = sortedRoles(state, guildId).filter(
    (role) => role.hoist && role.id !== guildId,
  );
  const visible = channelId
    ? members.filter((member) =>
        hasPermission(
          channelPermissions(state, channelId, member.user_id),
          PermissionFlags.VIEW_CHANNEL,
        ),
      )
    : members;
  const collator = new Intl.Collator('pt-BR', { sensitivity: 'base' });
  const byName = (a: MemberRecord, b: MemberRecord) =>
    collator.compare(
      selectDisplayName(state, a.user_id, guildId),
      selectDisplayName(state, b.user_id, guildId),
    );
  const groups = new Map<string, MemberRecord[]>();
  for (const member of visible) {
    const online = selectPresence(state, member.user_id) !== 'offline';
    const hoisted = online
      ? roles.find((role) => member.roles.includes(role.id))
      : undefined;
    const key = !online ? 'offline' : (hoisted?.id ?? 'online');
    groups.set(key, [...(groups.get(key) ?? []), member]);
  }
  const result: MemberGroup[] = [];
  for (const role of roles) {
    const list = groups.get(role.id);
    if (list?.length) {
      result.push({ id: role.id, role, members: list.sort(byName) });
    }
  }
  for (const key of ['online', 'offline']) {
    const list = groups.get(key);
    if (list?.length) {
      result.push({ id: key, role: null, members: list.sort(byName) });
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Caixa de entrada, threads seguidas e pessoas
// ---------------------------------------------------------------------------

/** Threads em que a pessoa entrou e que não estão arquivadas, a mais ativa primeiro. */
export function selectFollowedThreads(state: ChatState): APIChannel[] {
  return Object.keys(state.joinedThreads)
    .map((id) => state.channels[id])
    .filter(
      (thread) =>
        !!thread &&
        !thread.thread_metadata?.archived &&
        hasPermission(
          channelPermissions(state, thread.id),
          PermissionFlags.VIEW_CHANNEL,
        ),
    )
    .sort((a, b) =>
      compareSnowflakes(b.last_message_id ?? b.id, a.last_message_id ?? a.id),
    );
}

/**
 * Não lidas para a caixa de entrada: canais que a pessoa vê, threads em que
 * entrou e conversas diretas, sem os silenciados; a mais nova primeiro.
 */
export function selectUnreadChannels(
  state: ChatState,
  now: Date = new Date(),
): APIChannel[] {
  const ids = new Set<Snowflake>(state.privateChannelIds);
  for (const guildId of state.guildIds) {
    if (selectIsGuildMuted(state, guildId, now)) continue;
    for (const id of state.guildChannelIds[guildId] ?? []) ids.add(id);
  }
  for (const id of Object.keys(state.joinedThreads)) ids.add(id);
  return [...ids]
    .map((id) => state.channels[id])
    .filter(
      (channel) =>
        !!channel &&
        selectIsUnread(state, channel.id) &&
        !selectIsChannelMuted(state, channel.id, now) &&
        (!channel.guild_id ||
          hasPermission(
            channelPermissions(state, channel.id),
            PermissionFlags.VIEW_CHANNEL | PermissionFlags.READ_MESSAGE_HISTORY,
          )),
    )
    .sort((a, b) =>
      compareSnowflakes(b.last_message_id ?? b.id, a.last_message_id ?? a.id),
    );
}

export type DirectoryPerson = {
  userId: Snowflake;
  name: string;
  status: 'online' | 'idle' | 'dnd' | 'offline';
  /** Primeiro servidor em comum (para cargo e apelido). */
  guildId: Snowflake | null;
};

/**
 * Pessoas da empresa (o "Pessoas" do início, no lugar dos amigos do
 * Discord): todos os membros dos servidores da pessoa, sem ela mesma e sem
 * bots, em ordem de nome.
 */
export function selectDirectory(state: ChatState): DirectoryPerson[] {
  const people = new Map<Snowflake, DirectoryPerson>();
  for (const guildId of state.guildIds) {
    for (const userId of Object.keys(state.members[guildId] ?? {})) {
      if (people.has(userId) || userId === state.me?.id) continue;
      const user = state.users[userId];
      if (!user || user.bot) continue;
      people.set(userId, {
        userId,
        name: selectDisplayName(state, userId, guildId),
        status: selectPresence(state, userId),
        guildId,
      });
    }
  }
  const collator = new Intl.Collator('pt-BR', { sensitivity: 'base' });
  return [...people.values()].sort((a, b) => collator.compare(a.name, b.name));
}
